/**
 * Authentication Context
 * Manages user session state and authentication status
 */

import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { apiClient } from '../services/api';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (user: User) => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loginTimeRef = useRef<number>(Date.now());
  const hasCheckedSessionRef = useRef(false);

  // Check session on mount only once on app startup
  useEffect(() => {
    const initializeAuth = async () => {
      // Only check session once per app lifetime
      if (hasCheckedSessionRef.current) {
        return;
      }
      hasCheckedSessionRef.current = true;

      setIsLoading(true);
      try {
        console.debug('[Auth] Checking session on startup...');
        await checkSession();
      } catch (err) {
        console.debug('[Auth] Initial session check failed (expected if not logged in)');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const normalizeUser = (userData: any): User => {
    return {
      id: userData.id || userData._id || '',
      email: userData.email || '',
      name: userData.name || '',
      role: userData.role || 'user',
      institution: userData.institution || '',
      rateLimits: userData.rateLimits || { messagesPerHour: 100, tokensPerDay: 50000 },
      preferences: userData.preferences || { theme: 'light', defaultModel: '', autoContextManagement: false },
      createdAt: userData.createdAt || new Date().toISOString(),
      lastLogin: userData.lastLogin || new Date().toISOString(),
      termsAccepted: userData.termsAccepted ?? false,
    };
  };

  const updateUser = (updates: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  };

  const checkSession = async () => {
    try {
      setError(null);
      const sessionInfo = await apiClient.getSession();
      setUser(normalizeUser(sessionInfo.user));
    } catch (err: any) {
      console.debug('Session check error:', err?.message);
      // Don't clear user if login happened recently (within 10 seconds)
      const timeSinceLogin = Date.now() - loginTimeRef.current;
      if (timeSinceLogin > 10000) {
        // More than 10 seconds since login, safe to clear user
        setUser(null);
      }
      // If login was recent, keep the user that was set by login()
    }
  };

  const login = (userData: User) => {
    // Set user directly (for email/password login)
    // Normalize to fill in any missing fields from the signin response
    const normalizedUser = normalizeUser(userData);
    console.log('[Auth] Login called with user:', normalizedUser);
    setUser(normalizedUser);
    // Stop loading since user is now authenticated
    setIsLoading(false);
    // Record when login happened to prevent checkSession() from overwriting it
    loginTimeRef.current = Date.now();
  };

  const logout = async () => {
    try {
      setError(null);
      await apiClient.logout();
      setUser(null);
      // Redirect to homepage
      window.location.href = '/';
    } catch (err: any) {
      setError(err.error?.message || 'Failed to logout');
      throw err;
    }
  };

  const refreshSession = async () => {
    await checkSession();
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    refreshSession,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
